import {
  useEffect,
  useMemo,
  cloneElement,
  JSXElementConstructor,
  ReactElement,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';
import { withTheme } from '@mui/styles';
import { Loader } from '@kep-ui-kit/ui-components';

import { CommonLayout, StyledTitle } from '@selfserve/react-ui';
import {
  routesPaths,
  accountPaths,
  IResponse,
  responseTypes,
} from '@selfserve/react-utils';
import {
  storage,
  storageKeys,
  useAuth,
  useCurrentRoute,
  useCustomSearchParams,
  useStorage,
} from '@selfserve/react-hooks';

import { useSubscriptionOverviewRequest, SubscriptionOverview } from '../data';

import { AccountNavigation } from '../ui/account-navigation';
import { AccountTabInfo } from '../ui/account-tab-info';

const checklistBackendUrl = process.env['NX_CHECKLIST_BE_URL'];

const StyledAccountLayoutContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    marginTop: '70px',
    [theme.breakpoints.down('md')]: {
      marginTop: '42px',
      flexDirection: 'column',
    },
  })
);

const StyledMenuContainer = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flex: '0 0 428px',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    flexBasis: '360px',
    paddingLeft: '80px',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: 0,
    flex: '0 0 0',
  },
}));

const StyledChildrenContainer = styled('div')(({ theme }: { theme?: any }) => ({
  width: '100%',
  flex: 1,
  paddingRight: '120px',
  [theme.breakpoints.down('lg')]: {
    paddingRight: theme.spacing(3),
  },
  [theme.breakpoints.down('md')]: {
    flex: '0 0 0',
    marginTop: theme.spacing(3),
    padding: `0 ${theme.spacing(3)}`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: `0 ${theme.spacing(2)}`,
  },
}));

const StyledButtonsGroup = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '176px',
  [theme.breakpoints.down('lg')]: {
    marginLeft: 0,
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const StyledAccountLayoutTitle = styled(StyledTitle)`
  line-height: 42px;
`;

const StyledLoader = styled(Loader)(({ theme }: { theme?: any }) => ({
  transform: 'unset',
  position: 'absolute',
  top: 0,
}));

export interface IContextData {
  subscriptionOverviewData: SubscriptionOverview;
  handleSubscriptionOverviewRequest: (
    value: object
  ) => Promise<IResponse<SubscriptionOverview>>;
};

interface Props {
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  theme: any;
};

const AccountLayout = withTheme(({ children, theme }: Props) => {
  const {
    data,
    isError,
    isLoading,
    isFetched,
    refetch: handleSubscriptionOverviewRequest,
    remove,
  } = useSubscriptionOverviewRequest();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query] = useCustomSearchParams();
  const [checklist, setChecklist] = useState<any>(null);
  const [isChecklistInited, setIsChecklistInited] = useState(false);
  const [showPage, setShowPage] = useState(false);
  const [queryParams, setQueryParams] = useStorage(
    storage.LOCAL,
    storageKeys.QUERY_PARAMS,
    query,
  );
  const [, setPartnerId] = useStorage(storage.LOCAL, storageKeys.PARTNER_ID);
  const { token } = useAuth();
  const { data: subscriptionOverviewData, status } = data || {};
  const { accountInfo } = subscriptionOverviewData || {};
  const { path: currentPath, name } = useCurrentRoute(accountPaths);
  const isOverview = useMemo(
    () => currentPath === accountPaths[0].path,
    [currentPath]
  );

  useEffect(() => {
    if (Object.keys(query || {}).length) {
      setQueryParams(query);
    }
  }, []);

  useEffect(() => {
    const Checklist = (window as any).Checklist;
    if (Checklist && !checklist) {
      setChecklist(new Checklist());
    }
  }, [isFetched]);

  useEffect(() => {
    checklist?.stop();
    checklist?.run();
  }, [pathname]);

  useEffect(() => {
    const handleChecklistItemClick = (event: any) => console.log(event.detail);
    const checklistKs = (() => {
      const [, account]: any = token?.split('.')?.map(value => {
        let decodedObject = {};
        try {
          const decodedToken = decodeURIComponent(atob(value));
          decodedObject = decodedToken ? JSON.parse(decodedToken) : {};
        } catch { }
        return decodedObject;
      }) || [];
      return null;
    })() || queryParams?.['ks'];

    if (!isChecklistInited && checklist && checklistKs) {
      setIsChecklistInited(true);

      checklist?.init({
        kaltura_application: 'cheklistTemplateOvervview',
        ks: checklistKs,
        apiUrl: checklistBackendUrl,
      });
      checklist?.run();
      window.addEventListener(
        checklist?.CHECKLIST_ITEM_CLICK_EVENT,
        handleChecklistItemClick,
      );

      navigate(pathname, { replace: true });
    }

    return () => {
      checklist?.stop();
      window.removeEventListener(
        checklist?.CHECKLIST_ITEM_CLICK_EVENT,
        handleChecklistItemClick
      );
    };
  }, [checklist]);

  useEffect(() => {
    const { partnerId } = subscriptionOverviewData?.accountInfo || {};
    if (!isError && String(status).startsWith(responseTypes.SUCCESS)) {
      setPartnerId(partnerId);
      setShowPage(true);
    } else if (isError || status) {
      navigate(routesPaths.LOGIN);
      setPartnerId(null);
      remove();
    }
  }, [navigate, isError, status]);

  return (
    <>
      <Helmet>
        <title>{t('titles.account-management')}</title>
      </Helmet>
      <CommonLayout displayName={accountInfo?.accountName}>
        {showPage && (
          <StyledAccountLayoutContainer>
            <StyledMenuContainer>
              <StyledButtonsGroup>
                <AccountNavigation
                  isOverview={isOverview}
                  currentPath={currentPath as string}
                />
              </StyledButtonsGroup>
            </StyledMenuContainer>
            {isLoading ? (
              <StyledLoader size="large" />
            ) : (
              <StyledChildrenContainer>
                <AccountTabInfo isOverview={isOverview} name={name} />
                {cloneElement(children, {
                  context: {
                    subscriptionOverviewData,
                    handleSubscriptionOverviewRequest,
                  },
                })}
              </StyledChildrenContainer>
            )}
          </StyledAccountLayoutContainer>
        )}
      </CommonLayout>
    </>
  );
});

export { AccountLayout };
